import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Snowflake,
  Refrigerator,
  WashingMachine,
  Shirt,
  Wind,
  GlassWater,
  Check,
  MessageCircle,
} from "lucide-react";
import { PROBLEM_CATEGORIES, WA_LINK, waLink } from "@/lib/constants";

const ICONS = { Snowflake, Refrigerator, WashingMachine, Shirt, Wind, GlassWater };

export default function Problems() {
  const [selected, setSelected] = useState(null);

  return (
    <section id="masalah" data-testid="problems-section" className="py-20 lg:py-40 border-t border-ink/10">
      <div className="max-w-7xl mx-auto px-5 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-12 lg:mb-20 text-center"
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-brand-ink mb-4">( Masalah Umum )</p>
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-4xl md:text-6xl lg:text-7xl">
            Masalah yang sering
            <br />
            <span className="font-serif italic normal-case text-brand-ink">ditangani</span>
          </h2>
          <p className="mx-auto mt-5 max-w-md text-sm text-ink/50">
            Pilih masalah peralatan Anda, lalu konsultasikan langsung via WhatsApp.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {PROBLEM_CATEGORIES.map((cat, i) => {
            const Icon = ICONS[cat.icon];
            return (
              <motion.div
                key={cat.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
                data-testid={`problem-card-${i}`}
                className="rounded-2xl lg:rounded-none border border-ink/10 bg-white p-5 lg:p-7 hover:border-brand transition-colors duration-500"
              >
                <div className="flex items-center gap-3 mb-4 lg:mb-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand/15 text-brand-ink">
                    <Icon size={20} />
                  </span>
                  <h3 className="font-display font-extrabold uppercase tracking-tight text-base lg:text-lg">{cat.title}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {cat.items.map((item, j) => {
                    const isSel = selected?.cat === cat.title && selected?.item === item;
                    return (
                      <button
                        key={item}
                        type="button"
                        data-testid={`problem-chip-${i}-${j}`}
                        aria-pressed={isSel}
                        onClick={() => setSelected(isSel ? null : { cat: cat.title, item })}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs lg:text-sm transition-colors duration-200 ${
                          isSel
                            ? "border-brand bg-brand/15 text-brand-ink font-semibold"
                            : "border-ink/10 text-ink/60 hover:border-brand/50"
                        }`}
                      >
                        {isSel && <Check size={13} />}
                        {item}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12 lg:mt-14 text-center">
          <AnimatePresence mode="wait">
            {selected ? (
              <motion.div
                key="selected"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="inline-flex flex-col items-center gap-4"
              >
                <p data-testid="problem-selected-label" className="text-sm text-ink/55">
                  Dipilih: <span className="font-semibold text-ink">{selected.cat}</span> — {selected.item}
                </p>
                <a
                  href={waLink(
                    `Halo Sanjaya Service, peralatan ${selected.cat} saya bermasalah: ${selected.item}. Mohon informasi dan jadwal teknisi.`
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="problem-whatsapp-cta"
                  aria-label="Konsultasikan masalah ini via WhatsApp"
                  className="inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-display font-bold text-black hover:bg-ink hover:text-white transition-colors duration-300"
                >
                  <MessageCircle size={18} strokeWidth={2.5} />
                  Konsultasikan Masalah Ini
                </a>
              </motion.div>
            ) : (
              <motion.a
                key="default"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="problems-whatsapp-button"
                aria-label="Konsultasi masalah peralatan via WhatsApp"
                className="inline-flex items-center gap-2 rounded-full border-2 border-brand px-8 py-4 font-display font-bold text-brand-ink hover:bg-brand hover:text-black transition-colors duration-300"
              >
                <MessageCircle size={18} strokeWidth={2.5} />
                Konsultasi via WhatsApp
              </motion.a>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
